import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ── Design tokens (Linear adapted — acid-lime → #2563eb) ──────────────────────
const L = {
  void:     '#08090a',
  carbon:   '#0f1011',
  obsidian: '#161718',
  graphite: '#23252a',
  smoke:    '#383b3f',
  ash:      '#62666d',
  fog:      '#8a8f98',
  mist:     '#d0d6e0',
  paper:    '#ffffff',
  blue:     '#2563eb',
  blueDim:  '#1d4ed8',
};

function useIsMobile(bp = 640) {
  const [m, setM] = useState(() => typeof window !== 'undefined' ? window.innerWidth <= bp : false);
  useEffect(() => {
    const fn = () => setM(window.innerWidth <= bp);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, [bp]);
  return m;
}

const inter = `-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif`;

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const FolderIcon   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>;
const CalendarIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const UsersIcon    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const TaskIcon     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>;
const DollarIcon   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
const LayersIcon   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>;
const StarIcon     = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="#f59e0b" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
const BellIcon     = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
const CheckIcon    = () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={L.blue} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar({ onIngresar, onComenzar }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <header style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
      background: scrolled || menuOpen ? 'rgba(8,9,10,0.92)' : 'transparent',
      backdropFilter: scrolled || menuOpen ? 'blur(20px)' : 'none',
      borderBottom: scrolled || menuOpen ? `0.5px solid ${L.graphite}` : 'none',
      transition: 'all 0.2s',
      fontFamily: inter,
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', height: 56, display: 'flex', alignItems: 'center', padding: `0 ${isMobile ? 20 : 32}px` }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 'auto' }}>
          <img src="/logo.svg" alt="Mi Estudio" width="26" height="26" style={{ objectFit: 'contain', flexShrink: 0 }} />
          <span style={{ fontSize: 15, fontWeight: 510, color: L.paper, letterSpacing: '-0.011em' }}>Mi Estudio</span>
        </div>

        {!isMobile && (
          <nav style={{ display: 'flex', gap: 4, position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
            {[['Funciones', '#funciones'], ['Precios', '#precios'], ['FAQ', '#faq']].map(([l, h]) => (
              <a key={l} href={h} style={{ fontSize: 13, color: L.fog, textDecoration: 'none', fontWeight: 400, padding: '8px 12px', borderRadius: 6, transition: 'color 0.15s' }}
                onMouseEnter={e => e.target.style.color = L.mist}
                onMouseLeave={e => e.target.style.color = L.fog}
              >{l}</a>
            ))}
          </nav>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!isMobile && (
            <button onClick={onIngresar} style={{
              background: L.paper, color: L.void, border: 'none',
              borderRadius: 9999, padding: '7px 16px',
              fontSize: 13, fontWeight: 510, cursor: 'pointer',
              letterSpacing: '-0.011em',
            }}>
              Ingresar
            </button>
          )}
          <button onClick={onComenzar} style={{
            background: L.blue, color: L.paper, border: 'none',
            borderRadius: 6, padding: isMobile ? '7px 14px' : '7px 16px',
            fontSize: 13, fontWeight: 510, cursor: 'pointer',
            letterSpacing: '-0.011em',
          }}>
            Probar gratis
          </button>
          {isMobile && (
            <button onClick={() => setMenuOpen(o => !o)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: L.mist, fontSize: 20, padding: '4px 8px', display: 'flex', alignItems: 'center' }}>
              {menuOpen ? '✕' : '☰'}
            </button>
          )}
        </div>
      </div>

      {isMobile && menuOpen && (
        <div style={{ background: L.carbon, borderTop: `0.5px solid ${L.graphite}`, padding: '8px 20px 20px' }}>
          {[['Funciones', '#funciones'], ['Precios', '#precios'], ['FAQ', '#faq']].map(([l, h]) => (
            <a key={l} href={h} onClick={() => setMenuOpen(false)}
              style={{ display: 'block', padding: '13px 0', fontSize: 15, fontWeight: 510, color: L.mist, textDecoration: 'none', borderBottom: `0.5px solid ${L.graphite}`, letterSpacing: '-0.011em' }}
            >{l}</a>
          ))}
          <button onClick={() => { setMenuOpen(false); onIngresar(); }}
            style={{ display: 'block', width: '100%', marginTop: 16, padding: '11px', background: L.paper, border: 'none', borderRadius: 9999, fontSize: 14, fontWeight: 510, color: L.void, cursor: 'pointer', letterSpacing: '-0.011em' }}
          >
            Ingresar a mi cuenta
          </button>
        </div>
      )}
    </header>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────────
function Hero({ onComenzar }) {
  const isMobile = useIsMobile();

  return (
    <section style={{
      background: L.void,
      minHeight: '100vh',
      display: 'flex', alignItems: 'center',
      padding: isMobile ? '96px 20px 72px' : '120px 32px 96px',
      position: 'relative', overflow: 'hidden',
      fontFamily: inter,
    }}>
      {/* Atmospheric floor gradient — lo único decorativo permitido */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%',
        background: 'linear-gradient(to top, rgba(37,99,235,0.06) 0%, transparent 100%)',
        pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: isMobile ? 48 : 80, position: 'relative' }}>

        {/* Texto */}
        <div style={{ flex: 1, maxWidth: isMobile ? '100%' : 540 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(37,99,235,0.12)', border: `0.5px solid rgba(37,99,235,0.3)`, borderRadius: 9999, padding: '4px 12px', marginBottom: 28 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: L.blue }} />
            <span style={{ fontSize: 12, fontWeight: 510, color: L.blue, letterSpacing: '0.02em' }}>GESTOR JURÍDICO</span>
          </div>

          <h1 style={{
            fontSize: isMobile ? 40 : 64,
            fontWeight: 510,
            color: L.paper,
            lineHeight: 1.0,
            margin: '0 0 24px',
            letterSpacing: isMobile ? '-0.016em' : '-0.022em',
          }}>
            Tu estudio,{' '}
            <span style={{ color: L.mist }}>ordenado y siempre a mano.</span>
          </h1>

          <p style={{ fontSize: isMobile ? 15 : 17, color: L.fog, lineHeight: 1.6, margin: '0 0 14px', maxWidth: 480, letterSpacing: '-0.011em' }}>
            Mi Estudio centraliza tu agenda, clientes, trámites, expedientes y la contabilidad en una sola plataforma accesible desde cualquier lugar.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 36 }}>
            {['Sin instalar nada', 'Notificaciones en tu teléfono', 'Integrable con Google Calendar'].map(t => (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: L.ash }}>
                <CheckIcon />
                {t}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10, alignItems: isMobile ? 'stretch' : 'center' }}>
            <button onClick={onComenzar} style={{
              background: L.blue, color: L.paper, border: 'none',
              borderRadius: 6, padding: '10px 20px',
              fontSize: 14, fontWeight: 510, cursor: 'pointer',
              letterSpacing: '-0.011em', textAlign: 'center',
            }}
              onMouseEnter={e => e.currentTarget.style.background = L.blueDim}
              onMouseLeave={e => e.currentTarget.style.background = L.blue}
            >
              Probar gratis
            </button>
            <a href="#funciones" style={{
              background: 'transparent', color: L.mist,
              border: `1px solid ${L.graphite}`,
              borderRadius: 6, padding: '10px 20px',
              fontSize: 14, fontWeight: 400, cursor: 'pointer',
              letterSpacing: '-0.011em', textDecoration: 'none',
              textAlign: 'center',
            }}>
              Ver cómo funciona →
            </a>
          </div>
          <p style={{ marginTop: 16, fontSize: 12, color: L.ash, letterSpacing: '-0.011em' }}>15 días gratis · Cancelás cuando querés</p>
        </div>

        {/* App preview — solo desktop */}
        {!isMobile && (
          <div style={{ flex: 1, maxWidth: 520 }}>
            <div style={{
              background: L.carbon,
              borderRadius: 12,
              boxShadow: `rgb(35,37,42) 0px 0px 0px 1px inset, rgba(0,0,0,0.6) 0px 4px 32px 0px`,
              padding: 20,
              overflow: 'hidden',
            }}>
              {/* Window chrome */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
                {['#ff5f57','#febc2e','#28c840'].map(c => <div key={c} style={{ width: 9, height: 9, borderRadius: '50%', background: c }} />)}
                <div style={{ flex: 1, background: L.obsidian, borderRadius: 4, height: 18, marginLeft: 8, display: 'flex', alignItems: 'center', paddingLeft: 8 }}>
                  <span style={{ fontSize: 9, color: L.ash, fontFamily: inter }}>miestudiojuridico.com/app</span>
                </div>
              </div>

              {/* Alert */}
              <div style={{ background: 'rgba(239,68,68,0.08)', border: `0.5px solid rgba(239,68,68,0.2)`, borderRadius: 6, padding: '7px 10px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ color: '#ef4444' }}><BellIcon /></span>
                <span style={{ fontSize: 11, color: L.mist, fontFamily: inter }}>Vencimiento en 48 hs — García c/ López · Civil</span>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ width: 110, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {['Panel','Expedientes','Agenda','Clientes','Contabilidad'].map((l, i) => (
                    <div key={l} style={{
                      padding: '6px 8px', borderRadius: 4,
                      background: i === 0 ? 'rgba(37,99,235,0.18)' : 'transparent',
                      color: i === 0 ? L.mist : L.ash,
                      fontSize: 11, fontWeight: i === 0 ? 510 : 400,
                      fontFamily: inter,
                    }}>{l}</div>
                  ))}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    ['García c/ López', 'Civil', '#ef4444', 'Audiencia hoy'],
                    ['Ramos s/ divorcio', 'Familia', '#f59e0b', 'Sin novedades'],
                    ['Torres c/ Municipio', 'Laboral', '#3b82f6', 'Pericia pdte.'],
                    ['Méndez c/ GCBA', 'Adm.', '#22c55e', 'Sentencia firme'],
                  ].map(([titulo, fuero, dot, estado]) => (
                    <div key={titulo} style={{ background: L.obsidian, border: `0.5px solid ${L.graphite}`, borderRadius: 6, padding: '7px 9px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                        <div style={{ width: 5, height: 5, borderRadius: '50%', background: dot, flexShrink: 0 }} />
                        <div style={{ fontSize: 11, fontWeight: 510, color: L.mist, fontFamily: inter }}>{titulo}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <span style={{ fontSize: 10, color: L.blue, background: 'rgba(37,99,235,0.12)', borderRadius: 3, padding: '1px 5px', fontFamily: inter }}>{fuero}</span>
                        <span style={{ fontSize: 10, color: L.ash, fontFamily: inter }}>{estado}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ── Features ─────────────────────────────────────────────────────────────────
function Features() {
  const isMobile = useIsMobile();
  const features = [
    { Icon: UsersIcon, title: 'Clientes centralizados', tagline: 'Toda la información de cada cliente', desc: 'Ficha completa con datos de contacto, historial de causas vinculadas y seguimiento de cada relación profesional.' },
    { Icon: FolderIcon, title: 'Expedientes organizados', tagline: 'Cada causa, vinculada a su cliente', desc: 'Seguimiento completo de cada causa con estados, etapas procesales y notas, vinculado al cliente automáticamente.' },
    { Icon: LayersIcon, title: 'Todo conectado', tagline: 'Clientes → expedientes → tareas', desc: 'Cada cliente tiene sus expedientes. Cada expediente tiene sus tareas. Todo en red para que nunca pierdas el hilo.' },
    { Icon: TaskIcon, title: 'Tareas y pendientes', tagline: 'Delegá y controlá en tiempo real', desc: 'Asigná tareas a tu equipo, establecé prioridades y seguí el avance desde el panel o desde el celular.' },
    { Icon: DollarIcon, title: 'Contabilidad del estudio', tagline: 'Sabé exactamente cuánto ganás', desc: 'Registrá honorarios, gastos e ingresos por causa. Controlá la salud financiera en tiempo real.' },
    { Icon: CalendarIcon, title: 'Agenda y alertas', tagline: 'Nunca más perdés una fecha', desc: 'Vencimientos con alertas automáticas. Próximamente: sincronización con Google Calendar.' },
  ];

  return (
    <section id="funciones" style={{ padding: isMobile ? '72px 20px' : '96px 32px', background: L.void, fontFamily: inter }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 48 : 64 }}>
          <p style={{ fontSize: 12, fontWeight: 510, color: L.blue, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>Funciones</p>
          <h2 style={{ fontSize: isMobile ? 28 : 48, fontWeight: 510, color: L.paper, margin: '0 0 16px', letterSpacing: isMobile ? '-0.016em' : '-0.022em', lineHeight: 1 }}>
            Todo lo que tu estudio necesita
          </h2>
          <p style={{ fontSize: isMobile ? 14 : 16, color: L.fog, maxWidth: 460, margin: '0 auto', lineHeight: 1.5, letterSpacing: '-0.011em' }}>
            Sistema de gestión integral, diseñado para llevar el control centralizado desde cualquier parte.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? 1 : 1 }}>
          {features.map(({ Icon, title, tagline, desc }, idx) => (
            <div key={title} style={{
              background: L.carbon,
              border: `0.5px solid ${L.graphite}`,
              padding: '24px',
              borderRadius: isMobile ? 12 : (idx === 0 ? '12px 0 0 0' : idx === 2 ? '0 12px 0 0' : idx === 3 ? '0 0 0 12px' : idx === 5 ? '0 0 12px 0' : 0),
              transition: 'background 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = L.obsidian}
              onMouseLeave={e => e.currentTarget.style.background = L.carbon}
            >
              <div style={{ color: L.fog, marginBottom: 16 }}>
                <Icon />
              </div>
              <div style={{ fontSize: 14, fontWeight: 510, color: L.paper, marginBottom: 4, letterSpacing: '-0.011em' }}>{title}</div>
              <div style={{ fontSize: 11, fontWeight: 510, color: L.blue, marginBottom: 10, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{tagline}</div>
              <div style={{ fontSize: 13, color: L.fog, lineHeight: 1.6, letterSpacing: '-0.011em' }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── How It Works ──────────────────────────────────────────────────────────────
function HowItWorks({ onComenzar }) {
  const isMobile = useIsMobile();
  const steps = [
    { n: '01', title: 'Creá tu estudio', desc: 'Registrate con tu email. En menos de 15 minutos tenés el estudio configurado y listo para operar.' },
    { n: '02', title: 'Cargá tu práctica', desc: 'Ingresá clientes, expedientes y agenda. Si sabés usar el correo, sabés usar Mi Estudio.' },
    { n: '03', title: 'Trabajá desde donde estés', desc: 'Vos y tu equipo, siempre con la misma información actualizada en tiempo real.' },
  ];

  return (
    <section style={{ padding: isMobile ? '72px 20px' : '96px 32px', background: L.carbon, borderTop: `0.5px solid ${L.graphite}`, fontFamily: inter }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 48 : 64 }}>
          <p style={{ fontSize: 12, fontWeight: 510, color: L.blue, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>Cómo funciona</p>
          <h2 style={{ fontSize: isMobile ? 28 : 48, fontWeight: 510, color: L.paper, margin: '0 0 16px', letterSpacing: '-0.022em', lineHeight: 1 }}>
            Empezá en minutos, no en semanas
          </h2>
          <p style={{ fontSize: 16, color: L.fog, maxWidth: 400, margin: '0 auto', lineHeight: 1.5, letterSpacing: '-0.011em' }}>
            Sin onboarding complicado, sin migración dolorosa.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 24 }}>
          {steps.map(s => (
            <div key={s.n} style={{ padding: '28px 0', borderTop: `0.5px solid ${L.graphite}` }}>
              <div style={{ fontSize: 11, fontWeight: 510, color: L.ash, letterSpacing: '0.08em', marginBottom: 16 }}>{s.n}</div>
              <div style={{ fontSize: 17, fontWeight: 510, color: L.paper, marginBottom: 10, letterSpacing: '-0.011em' }}>{s.title}</div>
              <div style={{ fontSize: 14, color: L.fog, lineHeight: 1.65, letterSpacing: '-0.011em' }}>{s.desc}</div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 56 }}>
          <button onClick={onComenzar} style={{
            background: L.blue, color: L.paper, border: 'none',
            borderRadius: 6, padding: '11px 28px',
            fontSize: 14, fontWeight: 510, cursor: 'pointer',
            letterSpacing: '-0.011em',
            width: isMobile ? '100%' : 'auto',
          }}
            onMouseEnter={e => e.currentTarget.style.background = L.blueDim}
            onMouseLeave={e => e.currentTarget.style.background = L.blue}
          >
            Comenzar ahora — es gratis
          </button>
        </div>
      </div>
    </section>
  );
}

// ── Testimonials ──────────────────────────────────────────────────────────────
function Testimonials() {
  const isMobile = useIsMobile();
  const items = [
    { quote: 'Tenía los expedientes en papel, en el correo y en una hoja de Excel. Ahora todo está en un lugar y no volví a perder una audiencia.', name: 'Carla Mendizábal', role: 'Abogada independiente · Buenos Aires', initials: 'CM', bg: '#6d28d9' },
    { quote: 'Implementamos Mi Estudio con todo el equipo en una tarde. La asignación de tareas cambió completamente cómo organizamos el trabajo interno.', name: 'Estudio Ferreyra & Asociados', role: 'Derecho comercial y laboral · Córdoba', initials: 'FA', bg: '#0891b2' },
    { quote: 'Lo que más me sorprendió fue lo simple que es. En una semana usaba todas las funciones sin soporte. Y el celular es perfecto.', name: 'Pablo Ochoa', role: 'Abogado · Bogotá, Colombia', initials: 'PO', bg: '#059669' },
  ];

  return (
    <section style={{ padding: isMobile ? '72px 20px' : '96px 32px', background: L.void, borderTop: `0.5px solid ${L.graphite}`, fontFamily: inter }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 40 : 56 }}>
          <p style={{ fontSize: 12, fontWeight: 510, color: L.blue, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>Testimonios</p>
          <h2 style={{ fontSize: isMobile ? 26 : 40, fontWeight: 510, color: L.paper, margin: 0, letterSpacing: '-0.022em', lineHeight: 1.1 }}>
            Lo que dicen los estudios que ya lo usan
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12 }}>
          {items.map(({ quote, name, role, initials, bg }) => (
            <div key={name} style={{
              background: L.carbon,
              border: `0.5px solid ${L.graphite}`,
              borderRadius: 12,
              padding: '24px',
              display: 'flex', flexDirection: 'column', gap: 20,
            }}>
              <div style={{ display: 'flex', gap: 2 }}>
                {[...Array(5)].map((_, i) => <StarIcon key={i} />)}
              </div>
              <p style={{ fontSize: 14, color: L.mist, lineHeight: 1.7, margin: 0, flex: 1, letterSpacing: '-0.011em' }}>
                "{quote}"
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 510, color: L.paper, flexShrink: 0 }}>{initials}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 510, color: L.paper, letterSpacing: '-0.011em' }}>{name}</div>
                  <div style={{ fontSize: 12, color: L.ash, marginTop: 2, letterSpacing: '-0.011em' }}>{role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Pricing ───────────────────────────────────────────────────────────────────
function Pricing({ onComenzar }) {
  const isMobile = useIsMobile();
  const features = [
    '1 estudio · 1 colaborador incluido',
    'Expedientes y trámites ilimitados',
    'Agenda con alertas de vencimientos',
    'Gestión de clientes',
    'Asignación de tareas',
    'Contabilidad del estudio',
  ];

  return (
    <section id="precios" style={{ padding: isMobile ? '72px 20px' : '96px 32px', background: L.carbon, borderTop: `0.5px solid ${L.graphite}`, fontFamily: inter }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 40 : 56 }}>
          <p style={{ fontSize: 12, fontWeight: 510, color: L.blue, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>Precios</p>
          <h2 style={{ fontSize: isMobile ? 28 : 48, fontWeight: 510, color: L.paper, margin: '0 0 12px', letterSpacing: '-0.022em', lineHeight: 1 }}>
            Un solo precio, sin letra chica
          </h2>
          <p style={{ fontSize: 15, color: L.fog, margin: 0, letterSpacing: '-0.011em' }}>15 días de prueba sin límites. Cancelás cuando querés.</p>
        </div>

        <div style={{ maxWidth: 400, margin: '0 auto' }}>
          <div style={{
            background: L.obsidian,
            border: `1px solid ${L.blue}`,
            borderRadius: 12,
            padding: isMobile ? '28px 20px' : '32px 28px',
            boxShadow: `rgba(37,99,235,0.12) 0px 4px 32px`,
          }}>
            <div style={{ fontSize: 15, fontWeight: 510, color: L.paper, marginBottom: 4, letterSpacing: '-0.011em' }}>Mi Estudio</div>
            <div style={{ fontSize: 12, color: L.fog, marginBottom: 24, letterSpacing: '-0.011em' }}>Todo lo que necesitás para llevar tu estudio.</div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
              <span style={{ fontSize: isMobile ? 40 : 48, fontWeight: 510, color: L.paper, letterSpacing: '-0.022em', lineHeight: 1 }}>USD 10</span>
              <span style={{ fontSize: 13, color: L.fog }}>/ mes</span>
            </div>
            <div style={{ fontSize: 11, color: L.ash, marginBottom: 28, letterSpacing: '-0.011em' }}>Facturación mensual</div>

            <button onClick={onComenzar} style={{
              width: '100%', padding: '11px', borderRadius: 6,
              fontSize: 14, fontWeight: 510, cursor: 'pointer',
              background: L.blue, color: L.paper, border: 'none',
              marginBottom: 24, letterSpacing: '-0.011em',
              transition: 'background 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = L.blueDim}
              onMouseLeave={e => e.currentTarget.style.background = L.blue}
            >
              Probar gratis
            </button>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {features.map(f => (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: L.mist, letterSpacing: '-0.011em' }}>
                  <CheckIcon />
                  {f}
                </div>
              ))}
            </div>
          </div>
        </div>

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: L.ash, letterSpacing: '-0.011em' }}>
          ¿Más colaboradores o varios estudios?{' '}
          <a href="mailto:hola@miestudiojuridico.com" style={{ color: L.blue, textDecoration: 'none', fontWeight: 510 }}>Escribinos.</a>
        </p>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  const isMobile = useIsMobile();
  const items = [
    ['¿Puedo usar Mi Estudio desde el celular?', 'Sí. La plataforma es completamente responsiva y funciona en cualquier dispositivo con navegador web. No hay app para descargar ni nada que instalar.'],
    ['¿Qué tan segura es mi información?', 'Muy segura. Todos los datos se almacenan en la nube con cifrado en tránsito y en reposo. Los backups son automáticos.'],
    ['¿Qué pasa con mis datos si cancelo?', 'Tus datos son siempre tuyos. Si decidís cancelar, podés exportar toda tu información antes de cerrar la cuenta.'],
    ['¿Cómo funciona el sistema de roles?', 'Hay dos roles: Admin y Colaborador. El admin gestiona el estudio completo. Los colaboradores acceden solo a las secciones habilitadas.'],
    ['¿El precio está en dólares?', 'Sí, en USD para mantener consistencia. El cobro se realiza a través de Mercado Pago o tarjeta internacional.'],
  ];

  return (
    <section id="faq" style={{ padding: isMobile ? '72px 20px' : '96px 32px', background: L.void, borderTop: `0.5px solid ${L.graphite}`, fontFamily: inter }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 40 : 56 }}>
          <p style={{ fontSize: 12, fontWeight: 510, color: L.blue, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>FAQ</p>
          <h2 style={{ fontSize: isMobile ? 28 : 40, fontWeight: 510, color: L.paper, margin: 0, letterSpacing: '-0.022em', lineHeight: 1.1 }}>
            Preguntas frecuentes
          </h2>
        </div>
        <div>
          {items.map(([q, a], i) => (
            <div key={q} style={{ borderTop: `0.5px solid ${L.graphite}` }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: '100%', background: 'none', border: 'none',
                padding: '18px 0', display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', cursor: 'pointer', textAlign: 'left', gap: 16,
              }}>
                <span style={{ fontSize: isMobile ? 14 : 15, fontWeight: 510, color: open === i ? L.paper : L.mist, letterSpacing: '-0.011em', transition: 'color 0.15s' }}>{q}</span>
                <span style={{ fontSize: 18, color: L.fog, flexShrink: 0, transition: 'transform 0.2s', transform: open === i ? 'rotate(45deg)' : 'none', display: 'block', lineHeight: 1 }}>+</span>
              </button>
              {open === i && (
                <div style={{ paddingBottom: 20, fontSize: 14, color: L.fog, lineHeight: 1.7, letterSpacing: '-0.011em' }}>{a}</div>
              )}
            </div>
          ))}
          <div style={{ borderTop: `0.5px solid ${L.graphite}` }} />
        </div>
      </div>
    </section>
  );
}

// ── CTA Final ─────────────────────────────────────────────────────────────────
function CTAFinal({ onComenzar }) {
  const isMobile = useIsMobile();
  return (
    <section style={{
      background: L.carbon,
      padding: isMobile ? '72px 20px' : '120px 32px',
      textAlign: 'center',
      borderTop: `0.5px solid ${L.graphite}`,
      fontFamily: inter,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 600, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,99,235,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ maxWidth: 560, margin: '0 auto', position: 'relative' }}>
        <h2 style={{ fontSize: isMobile ? 30 : 48, fontWeight: 510, color: L.paper, margin: '0 0 16px', letterSpacing: isMobile ? '-0.016em' : '-0.022em', lineHeight: 1.1 }}>
          Tu próxima audiencia ya debería estar en el sistema.
        </h2>
        <p style={{ fontSize: isMobile ? 15 : 17, color: L.fog, margin: '0 0 40px', lineHeight: 1.6, letterSpacing: '-0.011em' }}>
          15 días de prueba gratis. Si no es para vos, cancelás en un clic.
        </p>
        <button onClick={onComenzar} style={{
          background: L.blue, color: L.paper, border: 'none',
          borderRadius: 6, padding: isMobile ? '12px 0' : '12px 36px',
          fontSize: 15, fontWeight: 510, cursor: 'pointer',
          letterSpacing: '-0.011em',
          width: isMobile ? '100%' : 'auto',
          transition: 'background 0.15s',
        }}
          onMouseEnter={e => e.currentTarget.style.background = L.blueDim}
          onMouseLeave={e => e.currentTarget.style.background = L.blue}
        >
          Crear mi estudio gratis
        </button>
        <p style={{ marginTop: 20, fontSize: 12, color: L.ash, letterSpacing: '-0.011em' }}>
          Precios en USD · Mercado Pago o tarjeta internacional
        </p>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer({ onIngresar }) {
  const isMobile = useIsMobile();
  return (
    <footer style={{ background: L.void, padding: isMobile ? '28px 20px' : '28px 32px', borderTop: `0.5px solid ${L.graphite}`, fontFamily: inter }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <img src="/logo.svg" alt="Mi Estudio" width="22" height="22" style={{ objectFit: 'contain' }} />
          <span style={{ fontSize: 14, fontWeight: 510, color: L.paper, letterSpacing: '-0.011em' }}>Mi Estudio</span>
        </div>
        <div style={{ fontSize: 12, color: L.ash, letterSpacing: '-0.011em' }}>
          © {new Date().getFullYear()} Mi Estudio Jurídico · miestudiojuridico.com
        </div>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          <a href="mailto:hola@miestudiojuridico.com" style={{ fontSize: 12, color: L.ash, textDecoration: 'none', letterSpacing: '-0.011em', transition: 'color 0.15s' }}
            onMouseEnter={e => e.target.style.color = L.mist}
            onMouseLeave={e => e.target.style.color = L.ash}
          >Contacto</a>
          <button onClick={onIngresar} style={{ background: 'none', border: 'none', fontSize: 12, color: L.ash, cursor: 'pointer', letterSpacing: '-0.011em', padding: 0, transition: 'color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.color = L.mist}
            onMouseLeave={e => e.currentTarget.style.color = L.ash}
          >
            Ingresar →
          </button>
        </div>
      </div>
    </footer>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function LandingDemo() {
  const navigate = useNavigate();
  const goApp      = () => navigate('/app');
  const goRegistro = () => navigate('/registro');

  return (
    <div style={{ fontFamily: inter, overflowX: 'hidden', background: L.void }}>
      <Navbar    onIngresar={goApp} onComenzar={goRegistro} />
      <Hero      onComenzar={goRegistro} />
      <Features />
      <HowItWorks onComenzar={goRegistro} />
      <Testimonials />
      <Pricing   onComenzar={goRegistro} />
      <FAQ />
      <CTAFinal  onComenzar={goRegistro} />
      <Footer    onIngresar={goApp} />
    </div>
  );
}
